/** Booking costs (#401 §8) as the member portal words them. */
import { formatKr } from "@nodus/booking";
import type { BookingCostFilters, BookingCostReason, BookingCostRow } from "@/lib/api/billing";
import { dayClock } from "@/lib/clock";
import { INVOICE_STATUS, formatPeriod } from "@/lib/invoices";

export const REASON_LABEL: Record<BookingCostReason, string> = {
  booked_time: "Booked time",
  checked_in_time: "Checked-in time",
  late_cancellation: "Late cancellation",
  no_show_fee: "No-show fee",
};
export const REASONS = Object.keys(REASON_LABEL) as BookingCostReason[];

/** No-show and late-cancellation rows get the orange badge. */
export const isFee = (reason: BookingCostReason): boolean => reason === "late_cancellation" || reason === "no_show_fee";

export const kr = (amount: string | number): string => `${formatKr(Number(amount))} kr`;

/** "2026-10" for a date (local calendar). */
export function monthParam(d: Date = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split("-").map(Number);
  return monthParam(new Date(y, m - 1 + delta, 1));
}

/** A month change invalidates the member/room filters — the backend builds the Select's option lists
 *  from that month's rows only, so a filter value from the old month can match nothing in the new one
 *  (an empty placeholder, "No charged bookings"). Drop ``member``/``room``; keep ``reason`` (its options
 *  are fixed, not month-dependent). */
export function resetFiltersForMonth(filters: Omit<BookingCostFilters, "month">): Omit<BookingCostFilters, "month"> {
  return { ...filters, member: null, room: null };
}

/** "2026-10" or "2026-10-01" → "Oct 2026". */
export function monthLabel(month: string): string {
  return formatPeriod(month.length === 7 ? `${month}-01` : month);
}

export function reasonText(row: BookingCostRow): string {
  if (row.reason === "checked_in_time" && row.checked_in_hours) return `Checked-in time (${Number(row.checked_in_hours)} h)`;
  if (row.reason === "late_cancellation" && row.cancelled_at)
    return `Late cancellation (cancelled ${dayClock(row.cancelled_at, row.site_time_zone)})`;
  return REASON_LABEL[row.reason];
}

export function invoiceText(row: BookingCostRow): string {
  if (row.invoice_state === "credited") return "Credited";
  if (row.invoice) {
    const name = row.invoice.number ? `Invoice ${row.invoice.number}` : "Invoice";
    return `${name} · ${INVOICE_STATUS[row.invoice.status].label}`;
  }
  if (row.invoice_state === "invoiced") return "Invoiced";
  return "Not invoiced yet";
}
